import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  MatAutocompleteModule,
  MatFormFieldModule,
  MatInputModule,
} from 'src/app/shared/material';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { SubmissionConstants } from '../../models/constants/submissions-constants';
import { ManualSubmissionService } from '../../services/manual-submissions-service';
import { merge } from 'rxjs';
import { ProfileService } from 'src/app/shared/services';

@Component({
  selector: 'manual-submission-dialog',
  imports: [
    MatDialogModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    MatAutocompleteModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './manual-submission-dialog.component.html',
  styleUrl: './manual-submission-dialog.component.scss',
})
export class ManualSubmissionDialogComponent implements OnInit {
  manualSubmissionTitle: string = this.submissionsConst.manualSubmissionTitle;
  manualSubmissionLabel: string = this.submissionsConst.manualSubmissionLabel;
  manualSubmissionPlaceholder: string =
    this.submissionsConst.manualSubmissionPlaceholder;

  createButtonText: string = this.submissionsConst.createButtonText;
  cancelButtonText: string = this.submissionsConst.cancelButtonText;

  validSerial: boolean = true;
  foundStatus: string = null;

  form: FormGroup;
  serialNumber: FormControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(20),
    this.noSpecialCharsValidator(),
  ]);

  errorMessage = signal('');
  loading: boolean = false;
  foundSubmission: any;
  private emailAddress: string;

  constructor(
    public submissionsConst: SubmissionConstants,
    private manualSubmissionService: ManualSubmissionService,
    private profileService: ProfileService,
    public dialogRef: MatDialogRef<ManualSubmissionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
  ) {
    merge(this.serialNumber.statusChanges, this.serialNumber.valueChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.updateErrorMessage());
  }
  ngOnInit(): void {
    this.profileService.getProfile().subscribe((profile) => {
      this.emailAddress = profile?.mail.toLocaleLowerCase() || '';
    });
  }

  updateErrorMessage() {
    if (this.serialNumber.hasError('required')) {
      this.errorMessage.set(this.submissionsConst.requiredErrMsg);
      return;
    }
    if (this.serialNumber.hasError('alphanumeric')) {
      this.errorMessage.set(this.submissionsConst.specialCharErrMsg);
    } else if (this.serialNumber.hasError('maxlength')) {
      this.errorMessage.set(this.submissionsConst.maxLengthErrMsg);
    }
  }

  onConfirm() {
    this.loading = true;
    this.manualSubmissionService
      .getManualSubmission(this.serialNumber.value, this.emailAddress)
      .subscribe({
        next: (res) => {
          this.foundStatus = res.status;
          this.loading = false;
          this.serialNumber.setValue(this.serialNumber.value);
          switch (res.status) {
            case this.submissionsConst.manualSubNotFound:
              this.serialNumber.setErrors({ submissionNotFound: true });
              this.errorMessage.set(
                `${this.serialNumber.value} does not exist.`,
              );
              break;
            case this.submissionsConst.manualSubExists:
              this.foundSubmission = res;
              break;
            case this.submissionsConst.manualSubCreated:
              this.foundSubmission = res;
              this.onNavigateToSubmission();
              break;
            case this.submissionsConst.manualSubNotAllowed:
              this.serialNumber.setErrors({ submissionNotAllowed: true });
              this.errorMessage.set(
                `Current user does not have access to submission ${this.serialNumber.value}`,
              );
              break;
            case this.submissionsConst.manualSubNotEnergyCreditsEnabled:
              this.serialNumber.setErrors({ notEnergyCreditsEnabled: true });
              this.errorMessage.set(
                res.errorMessage ||
                  `The facility for this serial number has not been enabled for Energy Credits.`,
              );
              break;
            case this.submissionsConst.manualSubFailed:
              this.serialNumber.setErrors({ submissionFailed: true });
              this.errorMessage.set(
                `Something went wrong when creating submission for ${this.serialNumber.value}. ${res.errorMessage}`,
              );
              break;
            default:
              break;
          }
        },
        error: () => {
          this.loading = false;
          this.serialNumber.setErrors({ submissionFailed: true });
          this.errorMessage.set(
            `Something went wrong when creating submission for ${this.serialNumber.value}.`,
          );
        },
      });
  }

  onCancel() {
    this.dialogRef.close();
  }

  onNavigateToSubmission() {
    if (!this.foundSubmission) {
      return;
    }
    this.dialogRef.close(this.foundSubmission);
  }

  noSpecialCharsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string;
      const alphanumericPattern = /^[a-zA-Z0-9]+$/;

      if (alphanumericPattern.test(value)) {
        return null;
      } else {
        return { alphanumeric: true };
      }
    };
  }
}
